import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, SlidersHorizontal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Vacations() {
  const { getAuthHeaders } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [logs, setLogs] = useState([]);
  const [filterEmp, setFilterEmp] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [form, setForm] = useState({ employee_id: '', date: todayStr(), days_used: '', note: '' });
  const [adjForm, setAdjForm] = useState({ employee_id: '', date: todayStr(), days_change: '', concept: '' });
  const [error, setError] = useState('');
  const [adjError, setAdjError] = useState('');

  const loadAll = async () => {
    try {
      const [e, l] = await Promise.all([
        axios.get(`${API}/api/timeclock/employees`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/timeclock/vacations${filterEmp ? `?employee_id=${filterEmp}` : ''}`, { headers: getAuthHeaders() }),
      ]);
      setEmployees(e.data);
      setLogs(l.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterEmp]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/api/timeclock/vacations`, {
        employee_id: form.employee_id,
        date: form.date,
        days_used: parseFloat(form.days_used) || 0,
        note: form.note,
      }, { headers: getAuthHeaders() });
      setShowForm(false);
      setForm({ employee_id: '', date: todayStr(), days_used: '', note: '' });
      loadAll();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const submitAdjustment = async (e) => {
    e.preventDefault();
    setAdjError('');
    const change = parseFloat(adjForm.days_change);
    if (isNaN(change) || change === 0) { setAdjError('Indica un número distinto de 0 (positivo o negativo)'); return; }
    if (!adjForm.concept.trim()) { setAdjError('El concepto es obligatorio'); return; }
    try {
      await axios.post(`${API}/api/timeclock/vacations/adjustment`, {
        employee_id: adjForm.employee_id,
        date: adjForm.date,
        days_change: change,
        concept: adjForm.concept,
      }, { headers: getAuthHeaders() });
      setShowAdjust(false);
      setAdjForm({ employee_id: '', date: todayStr(), days_change: '', concept: '' });
      loadAll();
    } catch (err) { setAdjError(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-3" data-testid="tc-vacations-section">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Filtrar colaborador</label>
            <select className="h-9 px-2 border border-slate-200 rounded-md text-sm min-w-[200px]"
              value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)}>
              <option value="">Todos</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.vacation_days} dias)</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setShowAdjust(true); setShowForm(false); }} variant="outline" className="border-[#AFBD20] text-[#1D3C6E] hover:bg-[#AFBD20]/10" data-testid="tc-adjust-vac-btn">
            <SlidersHorizontal className="w-4 h-4 mr-1" /> Ajuste de saldo
          </Button>
          <Button onClick={() => { setShowForm(true); setShowAdjust(false); }} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="tc-add-vacation-btn">
            <Plus className="w-4 h-4 mr-1" /> Registrar Vacacion
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 p-4 max-w-xl space-y-3" data-testid="tc-vacation-form">
          <h3 className="text-sm font-semibold text-[#1D3C6E]">Registrar vacación tomada</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block">Colaborador *</label>
              <select className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm"
                value={form.employee_id} required
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="tc-vac-emp">
                <option value="">Seleccionar</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name} (saldo: {e.vacation_days})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Fecha</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Dias (admite decimales)</label>
              <Input type="number" step="0.5" min="0.5" value={form.days_used} onChange={(e) => setForm({ ...form, days_used: e.target.value })} required data-testid="tc-vac-days" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Nota</label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="tc-vac-save">Guardar</Button>
          </div>
        </form>
      )}

      {showAdjust && (
        <form onSubmit={submitAdjustment} className="bg-white rounded-lg border-2 border-[#AFBD20] p-4 max-w-xl space-y-3" data-testid="tc-vacation-adjust-form">
          <h3 className="text-sm font-semibold text-[#1D3C6E]">Ajuste manual de saldo</h3>
          <p className="text-xs text-slate-500">
            Suma o resta días directamente al saldo del colaborador. Usa <strong>positivo</strong> para agregar días (ej. <code>1.5</code>) o <strong>negativo</strong> para descontar (ej. <code>-2</code>). Admite decimales.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block">Colaborador *</label>
              <select className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm"
                value={adjForm.employee_id} required
                onChange={(e) => setAdjForm({ ...adjForm, employee_id: e.target.value })} data-testid="tc-adj-emp">
                <option value="">Seleccionar</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name} (saldo: {e.vacation_days})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Fecha</label>
              <Input type="date" value={adjForm.date} onChange={(e) => setAdjForm({ ...adjForm, date: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Días (+ agrega / − descuenta) *</label>
              <Input type="number" step="0.01" value={adjForm.days_change}
                onChange={(e) => setAdjForm({ ...adjForm, days_change: e.target.value })}
                placeholder="ej. 1.5 o -2" required data-testid="tc-adj-days" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block">Concepto / motivo *</label>
              <Input value={adjForm.concept}
                onChange={(e) => setAdjForm({ ...adjForm, concept: e.target.value })}
                placeholder="ej. Bono aniversario, Corrección manual" required data-testid="tc-adj-concept" />
            </div>
          </div>
          {adjError && <p className="text-sm text-red-500">{adjError}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowAdjust(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="tc-adj-save">Guardar ajuste</Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Colaborador</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-right px-4 py-3">Cambio</th>
              <th className="text-right px-4 py-3">Saldo antes</th>
              <th className="text-right px-4 py-3">Días Vac. Restantes</th>
              <th className="text-left px-4 py-3">Registrado por</th>
              <th className="text-left px-4 py-3">Concepto / Nota</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-xs">Sin registros</td></tr>
            ) : logs.map(l => {
              const isAdj = l.type === 'adjustment';
              const change = (typeof l.days_change === 'number') ? l.days_change : -(l.days_used || 0);
              const changeColor = change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-slate-500';
              // Saldo restante = balance ACTUAL del empleado en la BD
              // (misma fuente que el reporte semanal). Si el empleado fue
              // eliminado, fallback al snapshot histórico de este registro.
              const liveEmp = employees.find(e => e.id === l.employee_id);
              const balRestante = liveEmp ? Number(liveEmp.vacation_days) : (l.balance_after ?? 0);
              const balAfterColor = balRestante < 0 ? 'text-rose-600' : 'text-slate-700';
              return (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tc-vac-row-${l.id}`}>
                  <td className="px-4 py-2.5">{l.date}</td>
                  <td className="px-4 py-2.5 font-medium">{l.employee_name}</td>
                  <td className="px-4 py-2.5">
                    {isAdj ? (
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#AFBD20]/20 text-[#1D3C6E] font-semibold">Ajuste</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 font-semibold">Vacación</span>
                    )}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-medium ${changeColor}`}>
                    {change > 0 ? '+' : ''}{change.toFixed(2)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{(l.balance_before ?? 0).toFixed(2)}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${balAfterColor}`}>{balRestante.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{l.logged_by}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{l.concept || l.note || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
